<script lang="ts">
  import { t } from '$lib/i18n/index.svelte';
  import { downloadBytes } from '$lib/media/download';
  import { mediaBytes } from '$lib/media/fetch';
  import { defang } from '$lib/protocol/link-guard';
  import type { VoiceElement } from '$lib/protocol/types';

  import type { ElementContext } from './context';

  let { element, ctx }: { element: VoiceElement; ctx: ElementContext } = $props();

  let audio = $state<HTMLAudioElement | null>(null);
  let playing = $state(false);
  let loading = $state(false);
  let unplayable = $state(false);
  let fetched: { bytes: Uint8Array; contentType: string } | undefined;

  function canPlay(contentType: string): boolean {
    const probe = document.createElement('audio');
    const type = contentType.startsWith('audio/ogg') ? 'audio/ogg; codecs="opus"' : contentType;
    return probe.canPlayType(type) !== '';
  }

  async function ensureFetched(): Promise<boolean> {
    if (fetched !== undefined) {
      return true;
    }
    loading = true;
    fetched = await mediaBytes(ctx.accountId, element.mediaId);
    loading = false;
    return fetched !== undefined;
  }

  async function toggle(): Promise<void> {
    if (playing) {
      audio?.pause();
      return;
    }
    if (!(await ensureFetched()) || fetched === undefined) {
      return;
    }
    // Safari cannot decode Ogg Opus — a recording made on Firefox or an older
    // Chromium. Degrade to the transcript and a download; playback that fails is
    // not worth an error screen.
    if (!canPlay(fetched.contentType)) {
      unplayable = true;
      return;
    }
    if (audio === null) {
      const url = URL.createObjectURL(
        new Blob([fetched.bytes as BlobPart], { type: fetched.contentType }),
      );
      audio = new Audio(url);
      audio.onplay = () => (playing = true);
      audio.onpause = () => (playing = false);
      audio.onended = () => (playing = false);
      audio.onerror = () => {
        playing = false;
        unplayable = true;
      };
    }
    void audio.play().catch(() => {
      unplayable = true;
    });
  }

  async function saveFile(): Promise<void> {
    if (await ensureFetched()) {
      const extension = fetched!.contentType.startsWith('audio/ogg') ? 'ogg' : 'm4a';
      downloadBytes(fetched!.bytes, fetched!.contentType, `voice-${element.mediaId}.${extension}`);
    }
  }

  const seconds = $derived(Math.round(element.durationMs / 1000));
</script>

<div class="voice">
  {#if unplayable}
    <div class="fallback">
      <span>{t('el.voice.unplayable')}</span>
      <button onclick={() => void saveFile()}>{t('el.voice.download')}</button>
    </div>
  {:else}
    <div class="player">
      <button class="play" onclick={() => void toggle()} disabled={loading}>
        {playing ? '⏸' : loading ? '…' : '▶'}
      </button>
      <div class="wave" aria-hidden="true">
        {#if element.waveform.length > 0}
          {#each element.waveform as level, i (i)}
            <span style="height: {Math.max(8, level)}%"></span>
          {/each}
        {:else}
          <span style="height: 40%"></span><span style="height: 70%"></span><span
            style="height: 50%"
          ></span><span style="height: 80%"></span><span style="height: 45%"></span>
        {/if}
      </div>
      <span class="duration">{seconds}s</span>
    </div>
  {/if}
  {#if element.transcript !== undefined && element.transcript.length > 0}
    <p class="transcript">{defang(element.transcript)}</p>
  {/if}
</div>

<style>
  .player {
    display: flex;
    align-items: center;
    gap: 10px;
    min-width: 180px;
  }
  .play {
    width: 40px;
    height: 40px;
    border-radius: 50%;
    border: 0;
    background: var(--brand);
    color: var(--brand-ink);
    font-size: 16px;
    cursor: pointer;
    flex-shrink: 0;
  }
  .wave {
    flex: 1;
    display: flex;
    align-items: center;
    gap: 2px;
    height: 32px;
  }
  .wave span {
    flex: 1;
    min-width: 2px;
    background: var(--brand);
    opacity: 0.55;
    border-radius: 2px;
  }
  .duration {
    font-size: 12px;
    color: var(--ink-soft);
  }
  .transcript {
    margin: 6px 0 0;
    font-size: 14px;
    color: var(--ink-soft);
    white-space: pre-wrap;
  }
  .fallback {
    display: flex;
    flex-direction: column;
    gap: 6px;
    font-size: 14px;
    color: var(--ink-soft);
  }
  .fallback button {
    align-self: flex-start;
    border: 0;
    background: transparent;
    color: var(--brand);
    font-weight: 600;
    cursor: pointer;
    padding: 0;
  }
</style>
